export * as schemas from "./gen/schemas.gen";
export * as types from "./gen/types.gen";

import type { Client } from "@hey-api/client-fetch";
import * as services from "./gen/sdk.gen";
import { useClient } from "./client";
import { HttpExceptionModel, HttpValidationError } from "./gen";

// eslint-disable-next-line
type ServiceFunction<T> = T extends (options: infer O) => any
    ? O extends { client?: Client }
        ? (options: Omit<O, "client">) => ReturnType<T>
        : T
    : T;

type Services = Omit<
    {
        [K in keyof typeof services]: ServiceFunction<(typeof services)[K]>;
    },
    "client"
>;

/**
 * Use the services with our own client.
 */
export const useServices = ({
    opts,
    requires,
}: {
    opts?: {
        throwOnError: boolean;
    };
    requires: {
        auth: boolean;
        organization: boolean;
        stek: boolean;
    };
}): Services => {
    const client = useClient({ requires });

    // Set default values for opts.
    if (!opts) {
        opts = { throwOnError: true };
    }
    if (opts?.throwOnError === undefined) {
        opts.throwOnError = true;
    }

    return Object.fromEntries(
        Object.entries(services as Services).map(([key, service]) => [
            key,
            // eslint-disable-next-line
            (options: any) => service({ client, ...opts, ...options }),
        ])
    ) as Services;
};

/**
 * Default services.
 */
export const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

type ErrorWithDetail = {
    detail: Array<object>;
};

const errorHasDetailArray = (error: unknown): error is ErrorWithDetail => {
    return (
        error instanceof Error &&
        "detail" in error &&
        error.detail instanceof Array
    );
};

/**
 * Check whether error is a HttpValidationError.
 */
export const isHttpValidationError = (
    error: unknown
): error is HttpValidationError => {
    if (errorHasDetailArray(error)) {
        return "loc" in error.detail[0];
    } else {
        return false;
    }
};

/**
 * Check whether error is a isHttpExceptionModel.
 */
export const isHttpExceptionModel = (
    error: unknown
): error is HttpExceptionModel => {
    if (errorHasDetailArray(error)) {
        return "type" in error.detail[0];
    } else {
        return false;
    }
};
